import { useEffect, useRef } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { UserPrivateRequestNickname } from "@features/privateRequest/PrivateRequestBar/UserPrivateRequestNickname";

import { Button } from "@components/Button";
import { Image } from "@components/Image";
import { Stack } from "@components/Stack";
import { Typography } from "@components/Typography";

import { TGetPrivateRequestResponse } from "@api/privateRequests";

import {
	usePrivateRequestAcceptMutation,
	usePrivateRequestDeclineMutation,
	usePrivateRequestListQuery,
} from "@storage/queries/privateRequest";

import { cancelSong } from "@lib/audio/playAudio";

import { QUERY_KEYS } from "@configs/api";
import { DELAY_TIME_1000 } from "@configs/constants";
import { ICON_PATHS } from "@configs/icons";

const PrivateRequestPopup = ({ user, onClose }: { user: TGetPrivateRequestResponse; onClose: () => void }) => {
	const queryClient = useQueryClient();
	const privateRequestAcceptMutation = usePrivateRequestAcceptMutation();
	const privateRequestDeclineMutation = usePrivateRequestDeclineMutation();
	const isResponding = privateRequestAcceptMutation.isPending || privateRequestDeclineMutation.isPending;

	const handleYesClose = async () => {
		await privateRequestAcceptMutation.mutateAsync(user.id);
		setTimeout(async () => {
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BROADCAST_STATUS_DATA] });
		}, DELAY_TIME_1000);

		onClose();
	};

	const handleNoClose = async () => {
		await privateRequestDeclineMutation.mutateAsync(user.id);
		setTimeout(async () => {
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BROADCAST_STATUS_DATA] });
		}, DELAY_TIME_1000);

		onClose();
	};
	return (
		<div
			id="private_popup"
			className={`relative block w-94 overflow-hidden rounded-2xl px-6 pt-5 pb-6 shadow-[0_20px_50px_rgba(0,0,0,0.5)]
				before:absolute before:-top-1/2 before:-left-1/2 before:-z-docked before:h-[200%] before:w-[200%]
				before:animate-private-popup-rotate before:bg-white
				before:bg-[linear-gradient(#262028,#262028),linear-gradient(white,white)] before:bg-size-[60%,20%]
				before:bg-position-[0_0,100%_0,100%_100%,0_100%] before:bg-no-repeat before:content-[''] after:absolute
				after:top-1.5 after:left-1.5 after:-z-docked after:h-[calc(100%-12px)] after:w-[calc(100%-12px)]
				after:rounded-xl after:bg-[#262028] after:content-['']`}
		>
			<Stack direction="row" justify="between" align="center">
				<Stack direction="row" spacing={12} align="center">
					{user.isWithClientAudio ? (
						<Button
							shape="circle"
							type="success"
							className="size-8"
							icon={<Image width={32} height={32} src={ICON_PATHS.PRIVATE_AUDIO_REQUEST} alt="audio_request" />}
						/>
					) : (
						<Button
							shape="circle"
							type="primary"
							className="size-8"
							icon={<Image width={32} height={32} src={ICON_PATHS.PRIVATE_CHAT_REQUEST} alt="chat_request" />}
						/>
					)}
					<Typography weight="bold" padding="none">
						Private show request
					</Typography>
				</Stack>

				<button onClick={onClose} className="cursor-pointer border-none text-right outline-none hover:opacity-60">
					<Typography size="sm" weight="bold" padding="none">
						Later
					</Typography>
				</button>
			</Stack>

			<Stack spacing={4} align="center" wide className="my-3.5 rounded-lg bg-black py-3">
				<UserPrivateRequestNickname user={user} />
				<Typography type="p" padding="none" align="center">
					{user.balance?.toLocaleString("ru")} tk
				</Typography>
			</Stack>

			<Stack direction="row" spacing={16}>
				<Button
					type={user.isWithClientAudio ? "success" : "primary"}
					wide
					onClick={handleYesClose}
					disable={isResponding}
				>
					<Typography weight="bold" padding="none">
						Accept
					</Typography>
				</Button>

				<Button type="secondary" wide onClick={handleNoClose} disable={isResponding}>
					<Typography weight="bold" padding="none">
						Decline
					</Typography>
				</Button>
			</Stack>
		</div>
	);
};

export const PrivateRequestPopUpContainer = () => {
	const privateRequestListQuery = usePrivateRequestListQuery();
	const toastIdsRef = useRef<Set<number>>(new Set());

	useEffect(() => {
		if (privateRequestListQuery.length > 0) {
			privateRequestListQuery.forEach((request) => {
				toastIdsRef.current.add(request.id);
				toast.custom((t) => <PrivateRequestPopup user={request} onClose={() => toast.dismiss(t)} />, {
					id: request.id,
					unstyled: true,
					closeButton: false,
					duration: Infinity,
					position: "bottom-center",
					className: "rounded-2xl z-dropdown",
				});
			});
		} else {
			toastIdsRef.current.forEach((id) => toast.dismiss(id));
			toastIdsRef.current.clear();
			cancelSong();
		}
	}, [privateRequestListQuery]);

	return null;
};
